import * as THREE from 'three';

/**
 * ===========================================================================
 *  GROUND LAYER — terrain-conformed decals and shockwave rings
 * ===========================================================================
 *
 * A decal that is a single flat quad hovering over a battlefield with 6 m of
 * relief either floats or sinks. Every patch here is instead a small NxN grid
 * whose vertices are dropped onto `terrain.heightAt()` at spawn time, so a
 * scorch mark sits *in* the hill it was burned into. That costs (N+1)^2 height
 * samples once, and nothing afterwards — the animation (fade, ring expansion,
 * pulse) all happens in the fragment shader from a birth timestamp.
 *
 * All patches of one blend mode share ONE geometry and ONE draw call. Slots
 * recycle oldest-first through a ring cursor, which is exactly the fixed-size
 * pool the brief asks for.
 *
 * Modes
 *   0  TEXTURED   sample the decal atlas cell in `aT.xy`   (scorch, boot, scuff)
 *   1  RING       procedural annulus expanding over life   (shockwaves)
 *   2  GLOW       soft radial disc, optional pulse         (aura pools, fire)
 */

const VERT = /* glsl */`
attribute vec4 aP;    // birth, life, fadeIn, mode
attribute vec4 aT;    // atlas cell x, atlas cell y, thickness/param, spin
attribute vec4 aCol;  // rgb + alpha (normalised u8)

uniform float uTime;

varying vec2 vUv;
varying vec4 vCol;
varying vec4 vP;
varying vec4 vT;

void main() {
  float age = uTime - aP.x;
  float t = age / max(aP.y, 1e-4);
  if (age < 0.0 || t >= 1.0) {
    vUv = vec2(0.0); vCol = vec4(0.0); vP = vec4(0.0); vT = vec4(0.0);
    gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
    return;
  }
  vUv = uv; vCol = aCol; vP = vec4(t, aP.yzw); vT = aT;
  gl_Position = projectionMatrix * viewMatrix * vec4(position, 1.0);
}
`;

const FRAG = /* glsl */`
uniform sampler2D uMap;
uniform float uTime;
varying vec2 vUv;
varying vec4 vCol;
varying vec4 vP;    // t, life, fadeIn, mode
varying vec4 vT;    // cellX, cellY, param, spin

void main() {
  float t = vP.x;
  float fin = smoothstep(0.0, max(vP.z, 1e-4), t);
  int mode = int(vP.w + 0.5);
  vec3 rgb = vCol.rgb;
  float a = 0.0;

  if (mode == 1) {
    // expanding shockwave: a hot leading edge chased by a softer body
    float r  = length(vUv - 0.5) * 2.0;
    float rr = pow(t, 0.55);
    float th = max(vT.z * (0.30 + 0.70 * (1.0 - t)), 0.012);
    float d  = (r - rr) / th;
    float body = exp(-d * d);
    float lead = exp(-pow((r - rr + th * 0.30) / (th * 0.34), 2.0));
    float inner = smoothstep(rr, rr - 0.55, r) * 0.10;
    a = (body * 0.85 + lead * 0.75 + inner) * pow(1.0 - t, 1.35);
    rgb = mix(rgb, vec3(1.0), clamp(lead * 0.9, 0.0, 1.0));
  } else if (mode == 2) {
    float r = length(vUv - 0.5) * 2.0;
    float pulse = 0.78 + 0.22 * sin(uTime * 9.0 + vT.w);
    a = exp(-2.6 * r * r) * pulse * (1.0 - smoothstep(0.72, 1.0, t));
  } else {
    vec2 uv2 = (vUv * 0.96 + 0.02 + vT.xy) * 0.25;
    a = texture2D(uMap, uv2).a * (1.0 - smoothstep(0.70, 1.0, t));
  }

  a *= vCol.a * fin;
  if (a < 0.004) discard;
  gl_FragColor = vec4(rgb, a);
}
`;

export class GroundLayer {
  /**
   * @param {object} o { slots, grid, additive, map, name, renderOrder }
   */
  constructor(o = {}) {
    const slots = this.slots = Math.max(4, o.slots | 0 || 128);
    const G = this.G = Math.max(1, o.grid | 0 || 2);
    const vpp = (G + 1) * (G + 1);
    this.vpp = vpp;
    const V = slots * vpp;

    this.pos = new Float32Array(V * 3);
    this.uvs = new Float32Array(V * 2);
    this.aP = new Float32Array(V * 4);
    this.aT = new Float32Array(V * 4);
    this.aC = new Uint8Array(V * 4);

    const idx = new Uint32Array(slots * G * G * 6);
    let k = 0;
    for (let s = 0; s < slots; s++) {
      const b = s * vpp;
      for (let j = 0; j < G; j++) {
        for (let i = 0; i < G; i++) {
          const a = b + j * (G + 1) + i;
          idx[k++] = a; idx[k++] = a + 1; idx[k++] = a + G + 2;
          idx[k++] = a; idx[k++] = a + G + 2; idx[k++] = a + G + 1;
        }
      }
      for (let j = 0; j <= G; j++) {
        for (let i = 0; i <= G; i++) {
          const v = (b + j * (G + 1) + i) * 2;
          this.uvs[v] = i / G; this.uvs[v + 1] = j / G;
        }
      }
    }

    const g = new THREE.BufferGeometry();
    const dyn = (arr, size, norm = false) =>
      new THREE.BufferAttribute(arr, size, norm).setUsage(THREE.DynamicDrawUsage);
    this.attrPos = dyn(this.pos, 3);
    this.attrP = dyn(this.aP, 4);
    this.attrT = dyn(this.aT, 4);
    this.attrC = dyn(this.aC, 4, true);
    g.setAttribute('position', this.attrPos);
    g.setAttribute('uv', new THREE.BufferAttribute(this.uvs, 2));
    g.setAttribute('aP', this.attrP);
    g.setAttribute('aT', this.attrT);
    g.setAttribute('aCol', this.attrC);
    g.setIndex(new THREE.BufferAttribute(idx, 1));
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    this.geometry = g;

    this.material = new THREE.ShaderMaterial({
      uniforms: { uTime: { value: 0 }, uMap: { value: o.map || null } },
      vertexShader: VERT, fragmentShader: FRAG,
      transparent: true, depthTest: true, depthWrite: false,
      blending: o.additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      side: THREE.DoubleSide, toneMapped: false, fog: false,
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -6,
    });
    this.material.name = o.name || 'vfxGround';

    this.mesh = new THREE.Mesh(g, this.material);
    this.mesh.name = o.name || 'vfxGround';
    this.mesh.frustumCulled = false;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.renderOrder = o.renderOrder ?? 3;
    this.mesh.castShadow = this.mesh.receiveShadow = false;

    this.cursor = 0;
    this.maxExpiry = -1;
    this._lo = 0; this._hi = -1;
    this._ranges = [
      [{ start: 0, count: 0 }], [{ start: 0, count: 0 }],
      [{ start: 0, count: 0 }], [{ start: 0, count: 0 }],
    ];
  }

  /**
   * Stamp a patch. Everything is written once; the shader animates it.
   *
   * @param {object} t   world.terrain (for heightAt); may be null (flat y)
   * @param {number} x,z centre
   * @param {number} radius half-extent in metres
   * @param {number} yaw   rotation about Y
   * @param {number} now   current vfx clock
   * @param {object} o     { life, fadeIn, mode, cell, thick, r,g,b,a, lift }
   */
  stamp(t, x, z, radius, yaw, now, o) {
    const s = this.cursor;
    this.cursor = (this.cursor + 1) % this.slots;
    const G = this.G, vpp = this.vpp, base = s * vpp;
    const c = Math.cos(yaw), sn = Math.sin(yaw);
    const lift = o.lift ?? 0.055;
    const pos = this.pos, aP = this.aP, aT = this.aT, aC = this.aC;
    const life = o.life ?? 8, fadeIn = o.fadeIn ?? 0.05, mode = o.mode ?? 0;
    const cx = (o.cell ?? 0) % 4, cy = ((o.cell ?? 0) / 4) | 0;
    const thick = o.thick ?? 0.10, spin = o.spin ?? 0;
    const r8 = Math.min(255, Math.max(0, (o.r ?? 1) * 255)) | 0;
    const g8 = Math.min(255, Math.max(0, (o.g ?? 1) * 255)) | 0;
    const b8 = Math.min(255, Math.max(0, (o.b ?? 1) * 255)) | 0;
    const a8 = Math.min(255, Math.max(0, (o.a ?? 1) * 255)) | 0;

    for (let j = 0; j <= G; j++) {
      const ly = (j / G - 0.5) * 2 * radius;
      for (let i = 0; i <= G; i++) {
        const lx = (i / G - 0.5) * 2 * radius;
        const wx = x + lx * c - ly * sn;
        const wz = z + lx * sn + ly * c;
        const v = base + j * (G + 1) + i;
        const v3 = v * 3, v4 = v * 4;
        pos[v3] = wx;
        pos[v3 + 1] = (t ? t.heightAt(wx, wz) : 0) + lift;
        pos[v3 + 2] = wz;
        aP[v4] = now; aP[v4 + 1] = life; aP[v4 + 2] = fadeIn; aP[v4 + 3] = mode;
        aT[v4] = cx; aT[v4 + 1] = cy; aT[v4 + 2] = thick; aT[v4 + 3] = spin;
        aC[v4] = r8; aC[v4 + 1] = g8; aC[v4 + 2] = b8; aC[v4 + 3] = a8;
      }
    }
    const exp = now + life;
    if (exp > this.maxExpiry) this.maxExpiry = exp;
    if (this._hi < 0) { this._lo = s; this._hi = s; }
    else { if (s < this._lo) this._lo = s; if (s > this._hi) this._hi = s; }
    return s;
  }

  flush(now) {
    this.material.uniforms.uTime.value = now;
    // An empty layer must not cost a draw call, so it hides itself entirely.
    this.mesh.visible = now <= this.maxExpiry;
    if (this._hi < 0) return;
    const lo = this._lo * this.vpp, cnt = (this._hi - this._lo + 1) * this.vpp;
    const attrs = [this.attrPos, this.attrP, this.attrT, this.attrC];
    for (let k = 0; k < attrs.length; k++) {
      const at = attrs[k], sz = at.itemSize, r = this._ranges[k][0];
      at.updateRanges.length = 0;
      r.start = lo * sz; r.count = cnt * sz;
      at.updateRanges.push(r);
      at.needsUpdate = true;
    }
    this._lo = 0; this._hi = -1;
  }

  clear() {
    this.aP.fill(0);
    this.maxExpiry = -1;
    this.cursor = 0;
    this._lo = 0; this._hi = this.slots - 1;
  }

  dispose() { this.geometry.dispose(); this.material.dispose(); }
}
